export interface ISseMessage {
  data: string;
  event?: string;
  id?: string;
}

interface SseParserOptions {
  onEvent(event: ISseMessage): void;
  onRetry?(milliseconds: number): void;
  maxBufferSize?: number;
}

export interface ISseParser {
  feed(chunk: string): void;
  finish(): void;
  reset(): void;
}

export function createSseParser(options: SseParserOptions): ISseParser {
  const maxBufferSize = options.maxBufferSize ?? 1024 * 1024;
  let buffer = "";
  let data: string[] = [];
  let event: string | undefined;
  let id: string | undefined;

  const checkSize = (pending = buffer.length) => {
    const size = pending + data.reduce((total, line) => total + line.length, 0);
    if (size > maxBufferSize) throw new Error("SSE buffer limit exceeded");
  };

  const dispatch = () => {
    if (data.length) {
      options.onEvent({ data: data.join("\n"), ...(event ? { event } : {}), ...(id !== undefined ? { id } : {}) });
    }
    data = [];
    event = undefined;
  };

  const processLine = (line: string) => {
    if (!line) {
      dispatch();
      return;
    }
    if (line.startsWith(":")) return;

    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? "" : line.slice(separator + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "data") data.push(value);
    else if (field === "event") event = value;
    else if (field === "id" && !value.includes("\0")) id = value;
    else if (field === "retry" && /^\d+$/.test(value)) options.onRetry?.(Number(value));
    checkSize(0);
  };

  const process = (consumeFinalLine = false) => {
    let lineStart = 0;
    for (let index = 0; index < buffer.length; index += 1) {
      const character = buffer[index];
      if (character !== "\n" && character !== "\r") continue;
      if (character === "\r" && index === buffer.length - 1 && !consumeFinalLine) break;

      processLine(buffer.slice(lineStart, index));
      if (character === "\r" && buffer[index + 1] === "\n") index += 1;
      lineStart = index + 1;
    }

    buffer = buffer.slice(lineStart);
    if (consumeFinalLine && buffer) {
      processLine(buffer);
      buffer = "";
    }
    checkSize();
  };

  return {
    feed(chunk) {
      buffer += chunk;
      process();
    },
    finish() {
      process(true);
      dispatch();
    },
    reset() {
      buffer = "";
      data = [];
      event = undefined;
      id = undefined;
    },
  };
}
